import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount, currency = 'USD') {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(num) {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(num);
}

export function formatPercent(num, decimals = 1) {
  if (num === null || num === undefined) return '0%';
  return `${num.toFixed(decimals)}%`;
}

export function formatDate(date, options = {}) {
  if (!date) return '';
  const defaultOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return new Date(date).toLocaleDateString('en-US', { ...defaultOptions, ...options });
}

export function formatRelativeTime(date) {
  if (!date) return '';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
}

export function calculateROI(revenue, spend) {
  if (!spend || spend === 0) return 0;
  return ((revenue - spend) / spend) * 100;
}

export function calculateCTR(clicks, impressions) {
  if (!impressions || impressions === 0) return 0;
  return (clicks / impressions) * 100;
}

export function getDateRange(period) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const ranges = {
    today: {
      start: today,
      end: new Date(today.getTime() + 86400000 - 1),
    },
    yesterday: {
      start: new Date(today.getTime() - 86400000),
      end: new Date(today.getTime() - 1),
    },
    '7d': {
      start: new Date(today.getTime() - 7 * 86400000),
      end: new Date(today.getTime() + 86400000 - 1),
    },
    '30d': {
      start: new Date(today.getTime() - 30 * 86400000),
      end: new Date(today.getTime() + 86400000 - 1),
    },
    'this_month': {
      start: new Date(today.getFullYear(), today.getMonth(), 1),
      end: new Date(today.getTime() + 86400000 - 1),
    },
    'last_month': {
      start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      end: new Date(today.getFullYear(), today.getMonth(), 0),
    },
  };
  
  return ranges[period] || ranges['7d'];
}

export function truncateText(text, maxLength = 50) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function generateSlug(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function copyToClipboard(text) {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback
  const textarea = document.createElement('textarea');
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
  return Promise.resolve();
}

export function getDaysRemaining(endDate) {
  if (!endDate) return 0;
  const now = new Date();
  const end = new Date(endDate);
  const diffMs = end - now;
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}
